import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { Bar, BarChart, CartesianGrid, LabelList, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useAIConversationChatMessageQuery } from '../../../queries/AIConversation/useAIConversationChatMessageQuery';
import { Button } from '../../../web/components-v2/Button';
import { Collapsible, CollapsiblePanel, CollapsibleTrigger } from '../../../web/components-v2/Collapsible';
import { THEME } from '../../../web/components-v2/Theme/theme';
import { ChevronDown } from '../../../web/components/svgs/icons/ChevronDown';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { getRoleLabel } from './roleLabel';
import { styleSheet } from './styles';

interface IProps {
	conversationId: string | null;
}

/** One stacked bar in the token-usage chart — a single assistant (coach) message's usage. */
interface TokenDatum {
	label: string;
	input: number;
	output: number;
	total: number;
	model?: string;
}

/** One averaged role-transition row in the "time between turns" chart (e.g. "Rep → AI Coach"). */
interface TurnGapRow {
	key: string;
	label: string;
	avgSeconds: number;
	count: number;
	color: string;
}

/** Headline timing for a conversation, plus the per-transition rows that drive the bar chart. */
interface TurnGapStats {
	rows: TurnGapRow[];
	avgSeconds: number;
	maxSeconds: number;
	turns: number;
}

const CHART_AXIS_TICK = { fontSize: THEME.typography.fontSizes.xxs, fill: THEME.colors.text.main } as const;
const CHART_VALUE_LABEL = { fontSize: THEME.typography.fontSizes.xxs, fill: THEME.colors.text.main } as const;
const TOKEN_INPUT_COLOR = THEME.colors.primary.main;
const TOKEN_OUTPUT_COLOR = THEME.colors.secondary.main;

/** Fraction of the track the longest bar fills, leaving room for the trailing value label. */
const TURN_GAP_BAR_SCALE = 82;

/** Bar tint keyed by the speaker who *finished* the previous turn (the one being waited on). */
const turnGapColor = (role?: Api.AIChatRole): string => {
	if (role === Api.AIChatRole.Assistant) {
		return THEME.colors.chart.enchantment;
	}
	if (role === Api.AIChatRole.User) {
		return THEME.colors.chart.levBlue;
	}
	return THEME.colors.chart.charmGray;
};

/** Width reserved for the (vertical) category axis so labels like "Coach 1" aren't clipped. */
const CATEGORY_AXIS_WIDTH = 120;

/** Grows chart height with the number of rows so horizontal bars stay a readable thickness. */
const chartHeight = (rowCount: number): number => Math.max(120, rowCount * 44 + 60);

/** Rounds to a single decimal place for compact second values (e.g. `8.4`). */
const round1 = (value: number): number => Math.round(value * 10) / 10;

/**
 * Builds the token-usage dataset: one datum per ASSISTANT (coach) message that carries any token
 * metadata, in conversation order. The backend only populates usage on assistant turns and TTLs it
 * after ~30 days, so older/non-assistant messages are skipped. When the backend omits `totalTokenCount`
 * but provides input+output, the total is derived so the stacked bar height stays meaningful.
 */
const buildTokenData = (messages: Api.AIChatMessage[]): TokenDatum[] => {
	const data: TokenDatum[] = [];
	let coachIndex = 0;

	for (const message of messages) {
		if (message.role !== Api.AIChatRole.Assistant) {
			continue;
		}

		const input = message.inputTokenCount;
		const output = message.outputTokenCount;
		const total = message.totalTokenCount;
		const hasAny = typeof input === 'number' || typeof output === 'number' || typeof total === 'number';
		if (!hasAny) {
			continue;
		}

		coachIndex += 1;
		const inputValue = typeof input === 'number' ? input : 0;
		const outputValue = typeof output === 'number' ? output : 0;
		const totalValue = typeof total === 'number' ? total : inputValue + outputValue;

		data.push({
			label: `Coach ${coachIndex}`,
			input: inputValue,
			output: outputValue,
			total: totalValue,
			model: message.model,
		});
	}

	return data;
};

/**
 * Aggregates the time between consecutive turns into per-transition averages. For each consecutive
 * message pair the elapsed seconds (between `creationDate`s, clamped to >= 0) are bucketed by the
 * role transition (e.g. "Rep → AI Coach"), then averaged. Pairs with unparseable dates are skipped.
 * Also returns the conversation-wide average gap, the single longest pause, and the turn count so the
 * panel can show headline stats above the chart.
 */
const buildTurnGapStats = (messages: Api.AIChatMessage[]): TurnGapStats => {
	const groups = new Map<string, { label: string; color: string; seconds: number[] }>();
	const allSeconds: number[] = [];

	for (let i = 1; i < messages.length; i += 1) {
		const prev = messages[i - 1];
		const curr = messages[i];

		const prevTime = Date.parse(prev.creationDate ?? '');
		const currTime = Date.parse(curr.creationDate ?? '');
		if (Number.isNaN(prevTime) || Number.isNaN(currTime)) {
			continue;
		}

		const seconds = Math.max(0, (currTime - prevTime) / 1000);
		const key = `${prev.role ?? 'unknown'}>${curr.role ?? 'unknown'}`;
		const existing = groups.get(key);
		if (existing) {
			existing.seconds.push(seconds);
		} else {
			groups.set(key, {
				label: `${getRoleLabel(prev.role)} → ${getRoleLabel(curr.role)}`,
				color: turnGapColor(prev.role),
				seconds: [seconds],
			});
		}
		allSeconds.push(seconds);
	}

	const rows: TurnGapRow[] = Array.from(groups.entries()).map(([key, group]) => ({
		key,
		label: group.label,
		color: group.color,
		count: group.seconds.length,
		avgSeconds: round1(group.seconds.reduce((sum, value) => sum + value, 0) / group.seconds.length),
	}));

	return {
		rows,
		avgSeconds: allSeconds.length ? round1(allSeconds.reduce((sum, value) => sum + value, 0) / allSeconds.length) : 0,
		maxSeconds: allSeconds.length ? round1(Math.max(...allSeconds)) : 0,
		turns: messages.length,
	};
};

function TokenUsageTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload: TokenDatum }> }) {
	if (!active || !payload?.length) {
		return null;
	}
	const row = payload[0]?.payload;
	if (!row) {
		return null;
	}
	return (
		<div className={css(styleSheet.insightsTooltip)}>
			<div className={css(styleSheet.insightsTooltipTitle)}>{row.label}</div>
			<p className={css(styleSheet.insightsTooltipLine)}>{row.input} input</p>
			<p className={css(styleSheet.insightsTooltipLine)}>{row.output} output</p>
			<p className={css(styleSheet.insightsTooltipLine)}>{row.total} total</p>
			{row.model ? <p className={css(styleSheet.insightsTooltipLine)}>{row.model}</p> : null}
		</div>
	);
}

/**
 * The "time between turns" chart: headline stat cards (avg gap, longest pause, turn count) above a
 * lightweight horizontal bar per role transition, each bar tinted by the speaker being waited on and
 * scaled against the longest average so the relative pacing reads at a glance.
 */
function TurnGapChart({ stats }: { stats: TurnGapStats }) {
	const scale = Math.max(stats.maxSeconds, ...stats.rows.map(row => row.avgSeconds), 1);

	return (
		<div className={css(styleSheet.insightsSection)}>
			<dl className={css(styleSheet.insightsStats)}>
				<div className={css(styleSheet.insightsStat)}>
					<dt className={css(styleSheet.insightsStatLabel)}>Avg between turns</dt>
					<dd className={css(styleSheet.insightsStatValue)}>{stats.avgSeconds}s</dd>
				</div>
				<div className={css(styleSheet.insightsStat)}>
					<dt className={css(styleSheet.insightsStatLabel)}>Longest pause</dt>
					<dd className={css(styleSheet.insightsStatValue)}>{stats.maxSeconds}s</dd>
				</div>
				<div className={css(styleSheet.insightsStat)}>
					<dt className={css(styleSheet.insightsStatLabel)}>Turns</dt>
					<dd className={css(styleSheet.insightsStatValue)}>{stats.turns}</dd>
				</div>
			</dl>
			<div>
				<h4 className={css(styleSheet.insightsChartTitle)}>
					Time between turns <span className={css(styleSheet.insightsChartSuffix)}>· average seconds</span>
				</h4>
				<div className={css(styleSheet.turnGapChart)}>
					{stats.rows.map(row => (
						<div className={css(styleSheet.turnGapRow)} key={row.key}>
							<div className={css(styleSheet.turnGapLabel)}>{row.label}</div>
							<div className={css(styleSheet.turnGapTrack)}>
								<div
									className={css(styleSheet.turnGapBar)}
									style={{
										background: row.color,
										width: `${Math.max((row.avgSeconds / scale) * TURN_GAP_BAR_SCALE, 4)}%`,
									}}
								/>
								<span className={css(styleSheet.turnGapValue)}>{row.avgSeconds}s</span>
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	);
}

/**
 * Collapsible "Insights" section in the Coffee AI coaching review's scoring & details drawer, below the
 * metadata summary. Renders, when the data supports it:
 *
 *  1. Time between turns — headline timing stats plus a bar per role transition, each averaging the
 *     seconds elapsed between consecutive `creationDate`s for that transition.
 *  2. Token usage by message — a stacked recharts bar per assistant turn (input stacked under output,
 *     so bar height is total tokens), built only from assistant messages that carry token metadata.
 *
 * Reuses `useAIConversationChatMessageQuery` (already populated by `TranscriptPanel`); react-query
 * dedupes by key so this issues no extra network request. Renders nothing when no conversation is
 * selected or when neither section has data; if only one dataset has data, only that section shows.
 */
export function ConversationInsights({ conversationId }: IProps) {
	const [isOpen, setIsOpen] = React.useState(false);

	const {
		data: messages,
		isLoading,
		isError,
	} = useAIConversationChatMessageQuery({
		id: conversationId ?? '',
		enabled: Boolean(conversationId),
	});

	const tokenData = React.useMemo(() => buildTokenData(messages ?? []), [messages]);
	const turnGapStats = React.useMemo(() => buildTurnGapStats(messages ?? []), [messages]);

	// Nothing selected: render nothing (the transcript panel owns the "select a conversation" prompt).
	if (!conversationId) {
		return null;
	}

	if (isLoading) {
		return (
			<section className={css(styleSheet.insights)} aria-label='Insights'>
				<div className={css(styleSheet.insightsHeader)}>
					<h3 className={css(styleSheet.insightsTitle)}>Insights</h3>
				</div>
				<div className={css(styleSheet.insightsSpinner)}>
					<LoadingSpinner type='small' />
				</div>
			</section>
		);
	}

	const hasToken = tokenData.length > 0;
	const hasTurnGaps = turnGapStats.rows.length > 0;

	// Errors are surfaced by the query's `meta.toastErrorMessage`; with no data there is nothing to chart.
	if (isError || (!hasToken && !hasTurnGaps)) {
		return null;
	}

	return (
		<Collapsible open={isOpen} onOpenChange={setIsOpen} className={css(styleSheet.insights)}>
			<div className={css(styleSheet.insightsHeader)}>
				<h3 className={css(styleSheet.insightsTitle)}>Insights</h3>
				<CollapsibleTrigger render={<Button variant='ghost' size='icon' aria-label={isOpen ? 'Collapse' : 'Expand'} />}>
					<ChevronDown
						className={css(styleSheet.collapseChevron, isOpen ? styleSheet.collapseChevronOpen : null)}
						width={18}
						height={18}
					/>
				</CollapsibleTrigger>
			</div>
			<CollapsiblePanel>
				<div className={css(styleSheet.insightsBody)}>
					{hasTurnGaps ? <TurnGapChart stats={turnGapStats} /> : null}
					{hasToken ? (
						<div>
							<h4 className={css(styleSheet.insightsChartTitle)}>Token usage by message</h4>
							<ResponsiveContainer width='100%' height={chartHeight(tokenData.length)}>
								<BarChart data={tokenData} layout='vertical' margin={{ top: 8, right: 48, left: 8, bottom: 8 }}>
									<CartesianGrid strokeDasharray='3 3' stroke={THEME.colors.border.main} />
									<XAxis type='number' allowDecimals={false} stroke={THEME.colors.text.main} tick={CHART_AXIS_TICK} />
									<YAxis
										type='category'
										dataKey='label'
										width={CATEGORY_AXIS_WIDTH}
										stroke={THEME.colors.text.main}
										tick={CHART_AXIS_TICK}
										interval={0}
									/>
									<Tooltip content={<TokenUsageTooltip />} />
									<Legend />
									<Bar dataKey='input' name='Input' stackId='tokens' fill={TOKEN_INPUT_COLOR} radius={[4, 0, 0, 4]} />
									<Bar dataKey='output' name='Output' stackId='tokens' fill={TOKEN_OUTPUT_COLOR} radius={[0, 4, 4, 0]}>
										<LabelList dataKey='total' position='right' {...CHART_VALUE_LABEL} />
									</Bar>
								</BarChart>
							</ResponsiveContainer>
						</div>
					) : null}
				</div>
			</CollapsiblePanel>
		</Collapsible>
	);
}
